import { useMemo, useState } from "react";
import { ChevronDown, Upload, FileText, Loader2, Trash2 } from "lucide-react";
import { supabase } from "../lib/supabase";
import { computeScore, getLevel, levelToneClass } from "../lib/scoring";

function TextField({ field, value, onChange, readOnly }) {
  if (readOnly) {
    return (
      <div className="space-y-1">
        <label className="text-xs font-medium text-gray-400">{field.label}</label>
        <p className="text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2">
          {value || <span className="text-gray-300 italic">Sin respuesta</span>}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-gray-500">{field.label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(field.id, e.target.value)}
        placeholder={field.placeholder || ""}
        className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
      />
    </div>
  );
}

function TextareaField({ field, value, onChange, readOnly }) {
  if (readOnly) {
    return (
      <div className="space-y-1">
        <label className="text-xs font-medium text-gray-400">{field.label}</label>
        <p className="text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2 whitespace-pre-wrap">
          {value || <span className="text-gray-300 italic">Sin respuesta</span>}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-gray-500">{field.label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(field.id, e.target.value)}
        placeholder={field.placeholder || ""}
        rows={field.rows || 3}
        className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand resize-none"
      />
    </div>
  );
}

function SelectField({ field, value, onChange, readOnly }) {
  if (readOnly) {
    return (
      <div className="space-y-1">
        <label className="text-xs font-medium text-gray-400">{field.label}</label>
        <p className="text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2">
          {value || <span className="text-gray-300 italic">Sin selección</span>}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-gray-500">{field.label}</label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(field.id, e.target.value)}
          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand appearance-none bg-white"
        >
          <option value="">Selecciona...</option>
          {(field.options || []).map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
        <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
      </div>
    </div>
  );
}

function FileField({ field, value, onChange, readOnly, userId }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const fileInfo = value ? (() => {
    try { return JSON.parse(value); } catch { return null; }
  })() : null;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!["pdf", "docx"].includes(ext)) {
      setError("Solo se permiten archivos .pdf y .docx");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("El archivo no puede superar 10MB");
      return;
    }

    setError(null);
    setUploading(true);

    const path = `${userId}/${field.id}_${Date.now()}.${ext}`;
    const { error: upErr } = await supabase.storage
      .from("activity-files")
      .upload(path, file, { upsert: true });

    if (upErr) {
      setError("Error al subir: " + upErr.message);
      setUploading(false);
      return;
    }

    onChange(field.id, JSON.stringify({ path, name: file.name }));
    setUploading(false);
  };

  const handleRemove = async () => {
    if (!fileInfo?.path) return;
    await supabase.storage.from("activity-files").remove([fileInfo.path]);
    onChange(field.id, "");
  };

  const handleDownload = async () => {
    if (!fileInfo?.path) return;
    const { data } = await supabase.storage
      .from("activity-files")
      .createSignedUrl(fileInfo.path, 60);
    if (data?.signedUrl) window.open(data.signedUrl, "_blank");
  };

  if (readOnly) {
    return (
      <div className="space-y-1">
        <label className="text-xs font-medium text-gray-400">
          {field.label}
        </label>
        {fileInfo ? (
          <button
            onClick={handleDownload}
            className="flex items-center gap-2 text-sm text-brand hover:underline bg-gray-50 rounded-lg px-3 py-2 w-full text-left"
          >
            <FileText size={16} />
            {fileInfo.name}
          </button>
        ) : (
          <p className="text-sm text-gray-300 italic bg-gray-50 rounded-lg px-3 py-2">
            Sin documento
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-gray-500">
        {field.label}
      </label>
      {fileInfo ? (
        <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 bg-white">
          <FileText size={16} className="text-brand shrink-0" />
          <button
            onClick={handleDownload}
            className="text-sm text-gray-700 hover:text-brand truncate flex-1 text-left"
          >
            {fileInfo.name}
          </button>
          <button
            onClick={handleRemove}
            className="text-gray-400 hover:text-red-500 shrink-0"
            title="Eliminar archivo"
          >
            <Trash2 size={14} />
          </button>
        </div>
      ) : (
        <label className="flex items-center justify-center gap-2 border-2 border-dashed border-gray-200 rounded-lg px-4 py-4 cursor-pointer hover:border-brand/40 hover:bg-brand/5 transition-colors">
          {uploading ? (
            <>
              <Loader2 size={16} className="animate-spin text-brand" />
              <span className="text-sm text-gray-500">Subiendo...</span>
            </>
          ) : (
            <>
              <Upload size={16} className="text-gray-400" />
              <span className="text-sm text-gray-500">
                Subir documento (.pdf, .docx)
              </span>
            </>
          )}
          <input
            type="file"
            accept=".pdf,.docx"
            onChange={handleUpload}
            disabled={uploading}
            className="hidden"
          />
        </label>
      )}
      {error && (
        <p className="text-xs text-red-500">{error}</p>
      )}
    </div>
  );
}

function ScaleField({ field, value, onChange, readOnly }) {
  const options = [0, 1, 2];
  return (
    <div className="flex items-start justify-between gap-3 py-1">
      <label className="text-sm text-gray-700 flex-1">{field.label}</label>
      <div className="flex gap-1.5 shrink-0">
        {options.map((opt) => {
          const selected = String(value) === String(opt);
          return (
            <button
              key={opt}
              type="button"
              disabled={readOnly}
              onClick={() => onChange(field.id, String(opt))}
              className={`w-8 h-8 rounded-lg border text-sm font-semibold transition-colors ${
                selected
                  ? "bg-brand border-brand text-white"
                  : "bg-white border-gray-200 text-gray-400 hover:border-brand/40"
              } ${readOnly ? "cursor-default" : ""}`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ScoreSummary({ score, levels }) {
  const level = getLevel(score.total, levels);
  return (
    <div className="mt-4 rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 bg-gray-50 space-y-1.5">
        {score.groups.map((g, i) => (
          <div key={i} className="flex justify-between text-xs text-gray-500">
            <span>{g.label}</span>
            <span className="font-medium text-gray-700">
              {g.subtotal} / {g.max}
            </span>
          </div>
        ))}
        <div className="flex justify-between text-sm font-semibold text-gray-900 pt-1.5 border-t border-gray-200">
          <span>Total</span>
          <span>
            {score.total} / {score.max}
          </span>
        </div>
      </div>
      {level && (
        <div className={`px-4 py-3 border-t ${levelToneClass(level.tone)}`}>
          <p className="text-sm font-semibold">{level.label}</p>
          {level.description && (
            <p className="text-xs mt-0.5 opacity-90">{level.description}</p>
          )}
        </div>
      )}
    </div>
  );
}

function HeadingField({ field }) {
  return (
    <div className="pt-3 pb-1">
      <h4 className="text-sm font-semibold text-gray-800">{field.label}</h4>
      {field.subtitle && (
        <p className="text-xs text-gray-500 mt-0.5">{field.subtitle}</p>
      )}
    </div>
  );
}

export default function StructuredForm({ config, values, onChange, readOnly, userId }) {
  const fields = config?.fields || [];
  const score = useMemo(() => computeScore(fields, values), [fields, values]);

  const handleChange = (id, value) => {
    onChange({ ...values, [id]: value });
  };

  return (
    <div className="space-y-4">
      {config?.description && (
        <p className="text-sm text-gray-600 bg-gray-50 rounded-xl p-4 whitespace-pre-wrap">
          {config.description}
        </p>
      )}

      {fields.map((field, i) => {
        const key = field.id || `field_${i}`;
        const value = values[field.id] || "";

        switch (field.type) {
          case "text":
            return <TextField key={key} field={field} value={value} onChange={handleChange} readOnly={readOnly} />;
          case "textarea":
            return <TextareaField key={key} field={field} value={value} onChange={handleChange} readOnly={readOnly} />;
          case "select":
            return <SelectField key={key} field={field} value={value} onChange={handleChange} readOnly={readOnly} />;
          case "file":
            return <FileField key={key} field={field} value={value} onChange={handleChange} readOnly={readOnly} userId={userId} />;
          case "scale":
            return <ScaleField key={key} field={field} value={value} onChange={handleChange} readOnly={readOnly} />;
          case "heading":
            return <HeadingField key={key} field={field} />;
          default:
            return null;
        }
      })}

      {score.scaleCount > 0 && (
        <ScoreSummary score={score} levels={config?.levels} />
      )}
    </div>
  );
}
